import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader2, Send } from 'lucide-react';

// Confirmed: Reviewed under the ToS Compliance Filter.


// Public lead capture page — a faithful copy of the Monday.com "Leads" form.
// Reachable at /lead-form with no sign-in; supports ?lead_status=<status>
// exactly like the original wkf.ms link.

const DEFAULT_INDUSTRIES = [
  'Agriculture',
  'Asset Based LTL',
  'Consumer Goods',
  'Floral',
  'Floral & Non-Floral',
  'Food & Beverage',
  'Health Care',
  'Manufacturing',
  'Mass Market',
  'Non-Floral',
  'Plants',
  'Produce',
  'Retail',
  'Transportation / Logistics',
  'Wholesale',
  'Other'
];

const SERVICE_OPTIONS = [
  'Trailer Load', 'LTL', 'Transportation / Logistics', 'LTL Care Of', 'U.S. Customs Brokerage',
  'Domestic Air Freight', 'Air Freight', 'Consolidation', 'Brokered Truckloads', 'Asset Based Truckloads',
];

const inputCls = "w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400";
const labelCls = "block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5";

export default function LeadCaptureForm() {
  const [form, setForm] = useState({
    fullName: '', company: '', location: '', email: '', phone: '',
    industry: '', service: '', otherDetails: '', website: '', // website = honeypot
    smsConsent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [industryOptions, setIndustryOptions] = useState<string[]>(DEFAULT_INDUSTRIES);

  // Dynamically load industry configuration from database
  useEffect(() => {
    let active = true;
    fetch('/api/public/industries')
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(data => {
        if (active && Array.isArray(data) && data.length > 0) {
          setIndustryOptions(data);
        }
      })
      .catch(() => { /* keep defaults */ });
    return () => { active = false; };
  }, []);

  // Autocomplete states
  const [suggestions, setSuggestions] = useState<{ placeId: string; description: string }[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [sessionToken, setSessionToken] = useState<any>(null);

  // Dynamically load Google Maps script from server config
  useEffect(() => {
    let active = true;
    fetch('/api/public/config')
      .then(res => res.json())
      .then(data => {
        if (!active) return;
        const apiKey = data.googleMapsApiKey || (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY || '';
        if (!apiKey) {
          console.warn("Google Maps API key not configured. Address autocomplete disabled.");
          return;
        }

        if ((window as any).google?.maps) {
          setMapsLoaded(true);
          return;
        }

        // Check if script already exists
        const existing = document.getElementById('gmaps-script');
        if (existing) {
          const handleLoad = () => setMapsLoaded(true);
          existing.addEventListener('load', handleLoad);
          return;
        }

        const script = document.createElement('script');
        script.id = 'gmaps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places&v=weekly`;
        script.async = true;
        script.defer = true;
        script.onload = () => setMapsLoaded(true);
        document.head.appendChild(script);
      })
      .catch(err => {
        console.error("Failed to load Google Maps config:", err);
      });

    return () => {
      active = false;
    };
  }, []);


  // Initialize Session Token
  useEffect(() => {
    if (mapsLoaded && (window as any).google?.maps) {
      try {
        (window as any).google.maps.importLibrary("places").then((placesLib: any) => {
          if (placesLib?.AutocompleteSessionToken) {
            setSessionToken(new placesLib.AutocompleteSessionToken());
          }
        });
      } catch (err) {
        console.error("Failed to initialize AutocompleteSessionToken:", err);
      }
    }
  }, [mapsLoaded]);


  // Click outside to dismiss dropdown suggestions
  useEffect(() => {
    const handleGlobalClick = () => {
      setSuggestions([]);
    };
    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleLocationChange = async (val: string) => {
    setForm(prev => ({ ...prev, location: val }));

    if (!val || val.length < 3 || !mapsLoaded || !(window as any).google?.maps) {
      setSuggestions([]);
      return;
    }

    try {
      setLoadingSuggestions(true);
      const placesLib = await (window as any).google.maps.importLibrary("places") as any;
      if (!placesLib?.AutocompleteSuggestion) {
        setSuggestions([]);
        return;
      }

      const request = {
        input: val,
        sessionToken: sessionToken,
        includedRegions: ['us'],
      };

      const { suggestions: apiSuggestions } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      if (apiSuggestions) {
        const list = apiSuggestions.map((s: any) => ({
          placeId: s.placePrediction?.placeId || s.placeId,
          description: s.placePrediction?.text?.toString() || s.description || '',
        }));
        setSuggestions(list);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error("Autocomplete suggestions error:", err);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSelectSuggestion = async (placeId: string) => {
    if (!(window as any).google?.maps) return;

    try {
      setSuggestions([]);
      setLoadingSuggestions(true);
      const placesLib = await (window as any).google.maps.importLibrary("places") as any;
      const place = new placesLib.Place({ id: placeId });


      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress'],
        sessionToken: sessionToken,
      });

      // Reset the session token for future requests
      if (placesLib?.AutocompleteSessionToken) {
        setSessionToken(new placesLib.AutocompleteSessionToken());
      }

      // Extract components
      let streetNumber = '';
      let route = '';
      let city = '';
      let state = '';

      if (place.addressComponents) {
        for (const component of place.addressComponents) {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.longText || '';
          } else if (types.includes('route')) {
            route = component.shortText || component.longText || '';
          } else if (types.includes('locality')) {
            city = component.longText || '';
          } else if (types.includes('administrative_area_level_1')) {
            state = component.shortText || '';
          }
        }
      }

      const addressLine = `${streetNumber} ${route}`.trim();
      const formattedLocation = [addressLine, city, state].filter(Boolean).join(', ');

      setForm(prev => ({ ...prev, location: formattedLocation || place.formattedAddress || '' }));
    } catch (err) {
      console.error("Error fetching place details:", err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim()) { setError('Please enter your full contact name.'); return; }
    if (!form.company.trim()) { setError('Please enter your company name.'); return; }
    if (!form.location.trim()) { setError('Please enter a location.'); return; }
    if (!form.email.trim()) { setError('Please enter your email address.'); return; }
    if (!form.phone.trim()) { setError('Please enter your phone number.'); return; }
    if (!form.industry) { setError('Please select an industry.'); return; }
    if (!form.service) { setError('Please select a service.'); return; }

    setSubmitting(true);
    setError(null);
    try {
      const leadStatus = new URLSearchParams(window.location.search).get('lead_status') || '';
      const isEmbeddedVal = typeof window !== 'undefined' && (window.self !== window.top || window.location.search.includes('embed=true'));
      const res = await fetch('/api/public/lead-capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...form, 
          contact: form.company, // backward compatibility
          leadStatus,
          isEmbedded: isEmbeddedVal,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Submission failed. Please try again.');
      setDone(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Detect if running inside an iframe or with embed query parameter
  const isEmbedded = typeof window !== 'undefined' && (window.self !== window.top || window.location.search.includes('embed=true'));

  // Notify parent window (Wix or external site) of content height changes for dynamic iframe resizing
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sendHeight = () => {
      try {
        const height = document.body.scrollHeight || document.documentElement.scrollHeight;
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'ael-lead-form-resize', height }, '*');
        }
      } catch (_) {}
    };
    sendHeight();
    window.addEventListener('resize', sendHeight);
    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', sendHeight);
      observer.disconnect();
    };
  }, [done, suggestions]);

  return (
    <div className={`w-full flex justify-center ${isEmbedded ? 'bg-transparent py-4 px-2' : 'min-h-screen bg-slate-100 py-10 px-4 overflow-y-auto'}`}>
      <div className="w-full max-w-xl">
        <div className={`bg-white rounded-2xl ${isEmbedded ? 'shadow-md border border-slate-200' : 'shadow-xl border border-slate-200'} overflow-hidden`}>
          <div className="px-8 pt-8 pb-6 border-b border-slate-100">
            <img src="/logo.png" alt="Armellini Logistics" className="h-12 object-contain" />
            <p className="mt-6 text-lg font-black tracking-tight text-slate-900 leading-snug">
              Get Started With a Free Online Quote
            </p>
          </div>


          {done ? (
            <div className="p-10 text-center">
              <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto" />
              <h2 className="mt-4 text-lg font-bold text-slate-900">Thank you!</h2>
              <p className="mt-1 text-sm text-slate-500">
                Your information was submitted successfully. A member of the Armellini team will be in touch shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-5">
              <div>
                <label className={labelCls}>Full Contact Name <span className="text-red-500">*</span></label>
                <input type="text" required value={form.fullName} onChange={set('fullName')} placeholder="Jane Doe" className={inputCls} />
              </div>

              <div>
                <label className={labelCls}>Company Name <span className="text-red-500">*</span></label>
                <input type="text" required value={form.company} onChange={set('company')} placeholder="Company Name" className={inputCls} />
              </div>

              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <label className={labelCls}>Consignee Address <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={form.location}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  placeholder="Street Address, City, State"
                  className={inputCls}
                  autoComplete="off"
                />

                {/* Suggestions Dropdown */}
                {suggestions.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-xl max-h-60 overflow-y-auto z-50 divide-y divide-slate-100 dark:divide-slate-800">
                    {suggestions.map((s, idx) => (
                      <button
                        key={s.placeId || idx}
                        type="button"
                        onClick={() => handleSelectSuggestion(s.placeId)}
                        className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-xs text-slate-700 dark:text-slate-350 font-medium transition cursor-pointer"
                      >
                        {s.description}
                      </button>
                    ))}
                  </div>
                )}
                {loadingSuggestions && (
                  <div className="absolute right-3 top-[38px] flex items-center">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Contact Email Address <span className="text-red-500">*</span></label>
                  <input type="email" required value={form.email} onChange={set('email')} placeholder="name@company.com" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Contact Phone Number <span className="text-red-500">*</span></label>
                  <input type="tel" required value={form.phone} onChange={set('phone')} placeholder="(555) 555-5555" className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Industry <span className="text-red-500">*</span></label>
                  <select required value={form.industry} onChange={set('industry')} className={`${inputCls} cursor-pointer`}>
                    <option value="">Select…</option>
                    {industryOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Service <span className="text-red-500">*</span></label>
                  <select required value={form.service} onChange={set('service')} className={`${inputCls} cursor-pointer`}>
                    <option value="">Select…</option>
                    {SERVICE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls}>Other Details</label>
                <textarea rows={4} value={form.otherDetails} onChange={set('otherDetails')} placeholder="Lanes, volumes, timing — anything that helps us quote accurately." className={inputCls} />
              </div>

              {/* Honeypot — humans never see or fill this */}
              <input type="text" value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off"
                     style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} aria-hidden="true" />

              {/* SMS Opt-In & Privacy Statement / Terms of Use Consent */}
              <div className="pt-1">
                <label className="flex items-start gap-3 cursor-pointer select-none group">
                  <input
                    type="checkbox"
                    id="smsConsent"
                    name="smsConsent"
                    checked={form.smsConsent}
                    onChange={(e) => setForm(prev => ({ ...prev, smsConsent: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                  />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    By checking this box, you agree to receive SMS text messages from Armellini Logistics regarding quotes, shipments, account updates, and customer care. Message frequency varies. Message and data rates may apply. Reply STOP at any time to opt out or HELP for assistance. View our{' '}
                    <a
                      href="https://www.armellini.com/privacy-statement"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      Privacy Statement
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.armellini.com/terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      Terms of Use
                    </a>
                    .
                  </span>
                </label>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 text-xs font-semibold">{error}</div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-xl text-sm transition-all shadow-lg shadow-blue-500/20 cursor-pointer disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-4 font-mono uppercase tracking-widest">Armellini Logistics</p>
      </div>
    </div>
  );
}

